import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  MonitorHeart as MonitorIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import styled from 'styled-components';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import { getCurrentAuthUser } from '../../utils/auth';
import { getWallets, removeWallet } from '../../utils/api';
import type { Wallet } from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';

const WalletCard = styled(Card)`
  height: 100%;
`;

const AddressText = styled(Typography)`
  font-family: monospace;
  word-break: break-all;
  margin: 1rem 0;
  padding: 0.5rem;
  background: ${({ theme }) => theme.palette.background.default};
  border-radius: 8px;
`;

const StatusBadge = styled(Box)<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1rem;
  
  svg {
    font-size: 20px;
  }
  
  ${({ status, theme }) => {
    switch (status) {
      case 'active':
        return `
          background-color: ${theme.palette.success.light}20;
          color: ${theme.palette.success.main};
          border: 1px solid ${theme.palette.success.main}40;
          
          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 1; }
            100% { opacity: 0.6; }
          }
          
          svg {
            animation: pulse 2s ease-in-out infinite;
          }
        `;
      case 'pending':
        return `
          background-color: ${theme.palette.warning.light}20;
          color: ${theme.palette.warning.main};
          border: 1px solid ${theme.palette.warning.main}40;
          
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          svg {
            animation: rotate 2s linear infinite;
          }
        `;
      case 'expired':
        return `
          background-color: ${theme.palette.error.light}20;
          color: ${theme.palette.error.main};
          border: 1px solid ${theme.palette.error.main}40;
        `;
      default:
        return '';
    }
  }}
`;

const ActionButton = styled(Button)`
  margin-top: 1rem;
`;

const ActionButtonPrimary = styled(ActionButton)`
  flex: 2;  // Takes up more space than secondary button
`;

const ActionButtonSecondary = styled(ActionButton)`
  flex: 1;  // Takes up less space than primary button
  opacity: 0.9;
`;

const TimeRemainingText = styled(Typography)`
  color: ${({ theme }) => theme.palette.warning.main};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0.5rem;
  
  svg {
    font-size: 18px;
  }
`;

const ActionButtonContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 1rem;
`;

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'active':
      return <MonitorIcon />;
    case 'pending':
      return <PendingIcon />;
    case 'expired':
      return <ErrorIcon />;
    default:
      return null;
  }
};

const getWalletStatus = (wallet: Wallet, t: (key: string, params?: any) => string): { status: string; timeLeft?: string } => {
  if (!wallet.paid) {
    return { status: 'pending' };
  }

  if (wallet.expired) {
    return { status: 'expired', timeLeft: t('wallets.status.expired') };
  }

  return { status: 'active' };
};

const getStatusText = (wallet: Wallet, t: (key: string) => string): string => {
  const { status } = getWalletStatus(wallet, t);
  
  switch (status) {
    case 'active':
      return t('wallets.status.active');
    case 'pending':
      return t('wallets.status.pending');
    case 'expired':
      return t('wallets.status.expired');
    default:
      return status;
  }
};

const Wallets: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingWallet, setRemovingWallet] = useState<number | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const user = await getCurrentAuthUser();
        if (!user?.userId) {
          setError(null); // Clear any existing error
          setWallets([]); // Ensure wallets is empty
          setLoading(false);
          return;
        }

        setUserId(user.userId); // Store the userId
        const response = await getWallets(user.userId);
        if (response.status === 'success') {
          setWallets(response.wallets);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch wallets';
        // Only set error if it's not a "User not found" case
        if (!errorMessage.includes('User not found')) {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWallets();
  }, [navigate]);

  const handleNavigate = (walletId: number, page: string, paymentMethod?: string) => {
    if (page === 'payment') {
      const wallet = wallets.find(w => w.walletId === walletId);
      if (!wallet) return;

      // For Stripe payments, redirect to external Stripe payment page
      if (paymentMethod === 'STRIPE' || wallet.paymentInfo?.paidMethod === 'STRIPE') {
        // TODO: Replace with actual Stripe payment URL
        const stripePaymentUrl = `https://checkout.stripe.com/pay/${wallet.walletId}`;
        window.location.href = stripePaymentUrl;
        return;
      }

      // For crypto payments (XCH or STR), navigate to internal payment page
      navigate('/payment', {
        state: {
          walletId: wallet.walletId,
          masterKey: wallet.masterKey,
          paymentInfo: {
            ...wallet.paymentInfo,
            paidMethod: paymentMethod || wallet.paymentInfo?.paidMethod
          }
        }
      });
      return;
    }
    
    // Special handling for settings page to include walletId in URL
    if (page === 'settings') {
      navigate(`/settings/${walletId}`, { 
        state: { 
          walletId,
          masterKey: wallets.find(w => w.walletId === walletId)?.masterKey 
        } 
      });
      return;
    }

    // Special handling for dashboard page to include walletId in URL
    if (page === 'dashboard') {
      navigate(`/dashboard/${walletId}`, { 
        state: { 
          walletId,
          masterKey: wallets.find(w => w.walletId === walletId)?.masterKey 
        }
      });
      return;
    }
    
    navigate(`/${page}`, { state: { walletId } });
  };

  const handleRemoveWallet = async (walletId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering parent click handlers
    
    if (removingWallet || !userId) return; // Prevent multiple clicks and check for userId
    
    try {
      setRemovingWallet(walletId);
      await removeWallet(walletId, userId);
      // Remove the wallet from the local state
      setWallets(prevWallets => prevWallets.filter(w => w.walletId !== walletId));
    } catch (err) {
      console.error('Error removing wallet:', err);
      setError('Failed to remove wallet. Please try again.');
    } finally {
      setRemovingWallet(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography variant="h4" gutterBottom>
          {t('wallets.title')}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" paragraph>
          {t('wallets.subtitle')}
        </Typography>

        {error && error !== 'User not found' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={4}>
          {wallets.map((wallet: Wallet) => {
            const { status, timeLeft } = getWalletStatus(wallet, t);
            return (
              <Grid item xs={12} md={6} key={wallet.walletId}>
                <WalletCard>
                  <StatusBadge status={status}>
                    {getStatusIcon(status)}
                    {getStatusText(wallet, t)}
                  </StatusBadge>
                  
                  {timeLeft && (
                    <TimeRemainingText>
                      <AccessTimeIcon />
                      {timeLeft}
                    </TimeRemainingText>
                  )}
                  
                  <Typography variant="h6">
                    Wallet #{wallet.walletId}
                  </Typography>
                  
                  <AddressText variant="body2">
                    {wallet.masterKey}
                  </AddressText>

                  {!wallet.paid ? (
                    <ActionButtonContainer>
                      <ActionButton
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<PaymentIcon />}
                        onClick={() => handleNavigate(
                          wallet.walletId, 
                          'payment',
                          wallet.paymentInfo?.paidMethod
                        )}
                      >
                        {wallet.paymentInfo?.paidMethod === 'STRIPE' 
                          ? t('wallets.actions.payWithCard')
                          : t('wallets.actions.completeCryptoPayment')}
                      </ActionButton>
                      <Tooltip title={t('wallets.actions.removeWallet')}>
                        <IconButton
                          color="secondary"
                          onClick={(e) => handleRemoveWallet(wallet.walletId, e)}
                          disabled={!!removingWallet}
                          size="small"
                          sx={{ 
                            ml: 1,
                            opacity: 0.75,
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              opacity: 1
                            }
                          }}
                        >
                          {removingWallet === wallet.walletId ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </ActionButtonContainer>
                  ) : status === 'expired' ? (
                    <ActionButton
                      variant="contained"
                      color="primary"
                      fullWidth
                      startIcon={<PaymentIcon />}
                      onClick={() => handleNavigate(wallet.walletId, 'payment')}
                    >
                      {t('wallets.actions.renewMonitoring')}
                    </ActionButton>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <ActionButtonPrimary
                        variant="contained"
                        color="primary"
                        startIcon={<DashboardIcon />}
                        onClick={() => handleNavigate(wallet.walletId, 'dashboard')}
                      >
                        {t('wallets.actions.dashboard')}
                      </ActionButtonPrimary>
                      <ActionButtonSecondary
                        variant="outlined"
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => handleNavigate(wallet.walletId, 'settings')}
                      >
                        {t('wallets.actions.settings')}
                      </ActionButtonSecondary>
                    </Box>
                  )}
                </WalletCard>
              </Grid>
            );
          })}
        </Grid>

        {(wallets.length === 0 || error === 'User not found') && (
          <Box textAlign="center" py={8}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/')}
              startIcon={<AddIcon />}
              sx={{
                py: 2,
                px: 4,
                fontSize: '1.1rem'
              }}
            >
              {t('wallets.actions.addFirstWallet')}
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Wallets; 